import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Menu as MenuIcon, MoreVertical, GripVertical, ExternalLink } from 'lucide-react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMenus = () => {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    url: '',
    order: 0
  });

  const { getAuthHeaders } = useAuth();

  const fetchMenus = async () => {
    try {
      const response = await axios.get(`${API}/menus`);
      setMenus(response.data);
    } catch (error) {
      console.error('Failed to fetch menus:', error);
      toast.error('Failed to load menu links');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, []);

  const openCreateDialog = () => {
    setEditingMenu(null);
    setFormData({ 
      name: '', 
      url: '', 
      order: menus.length 
    });
    setDialogOpen(true);
  };

  const openEditDialog = (menu) => {
    setEditingMenu(menu);
    setFormData({
      name: menu.name,
      url: menu.url,
      order: menu.order
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Menu name is required');
      return;
    }

    if (!formData.url.trim()) {
      toast.error('Menu URL is required');
      return;
    }

    setSaving(true);

    try {
      if (editingMenu) {
        await axios.put(
          `${API}/menus/${editingMenu.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Menu link updated successfully');
      } else {
        await axios.post(
          `${API}/menus`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Menu link created successfully');
      }
      
      setDialogOpen(false);
      fetchMenus();
    } catch (error) {
      console.error('Failed to save menu:', error);
      toast.error('Failed to save menu link');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setDeleting(true);

    try {
      await axios.delete(`${API}/menus/${deleteId}`, { headers: getAuthHeaders() });
      toast.success('Menu link deleted successfully');
      fetchMenus();
    } catch (error) {
      console.error('Failed to delete menu:', error);
      toast.error('Failed to delete menu link');
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  return (
    <AdminLayout>
      <div className="page-transition" data-testid="admin-menus">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Manrope'] text-2xl md:text-3xl font-bold text-slate-900 mb-2">
              Menu Links
            </h1>
            <p className="text-slate-500">Manage navigation menu items displayed on the blog</p>
          </div>
          <Button 
            onClick={openCreateDialog}
            className="btn-primary rounded-full text-white"
            data-testid="create-menu-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Link
          </Button>
        </div>

        {/* Info Card */}
        <div className="bg-gradient-to-br from-[#6F42F0]/5 to-[#10B981]/5 rounded-2xl p-6 mb-8 border border-purple-100">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#6F42F0]/10 flex items-center justify-center flex-shrink-0">
              <MenuIcon className="w-5 h-5 text-[#6F42F0]" />
            </div>
            <div>
              <h3 className="font-['Manrope'] font-semibold text-slate-900 mb-1">
                About Menu Links
              </h3>
              <p className="text-slate-600 text-sm">
                These links appear in the main navigation of your blog. Use them to link to your main website, 
                app download pages, or other important pages. Links with lower order numbers appear first.
              </p>
            </div>
          </div>
        </div>

        {/* Menus List */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          {loading ? (
            <div className="p-8 space-y-4">
              {Array(3).fill(0).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="skeleton h-6 w-6 rounded"></div>
                  <div className="skeleton h-12 w-12 rounded-xl"></div>
                  <div className="flex-1">
                    <div className="skeleton h-5 w-32 mb-2 rounded"></div>
                    <div className="skeleton h-4 w-48 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : menus.length === 0 ? (
            <div className="p-12 text-center">
              <MenuIcon className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <h3 className="font-['Manrope'] text-lg font-semibold text-slate-900 mb-2">
                No menu links yet
              </h3>
              <p className="text-slate-500 mb-6">
                Add your first menu link to customize the navigation
              </p>
              <Button 
                onClick={openCreateDialog}
                className="btn-primary rounded-full text-white"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Link
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {menus.map((menu, index) => (
                <div
                  key={menu.id}
                  className="flex items-center gap-4 p-4 md:p-6 admin-table-row"
                  data-testid={`menu-row-${menu.id}`}
                >
                  {/* Order Indicator */}
                  <div className="text-slate-300">
                    <GripVertical className="w-5 h-5" />
                  </div>

                  {/* Order Number */}
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-sm font-medium flex-shrink-0">
                    {index + 1}
                  </div>

                  {/* Icon */}
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3B82F6]/10 to-[#60A5FA]/10 flex items-center justify-center flex-shrink-0">
                    <ExternalLink className="w-5 h-5 text-[#3B82F6]" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Manrope'] font-semibold text-slate-900 mb-1">
                      {menu.name}
                    </h3>
                    <a 
                      href={menu.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-[#6F42F0] hover:underline truncate block"
                    >
                      {menu.url}
                    </a>
                  </div>

                  {/* Actions */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-10 w-10"
                        data-testid={`menu-actions-${menu.id}`}
                      >
                        <MoreVertical className="w-5 h-5 text-slate-400" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem 
                        onClick={() => openEditDialog(menu)}
                        className="cursor-pointer"
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => setDeleteId(menu.id)}
                        className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">
                {editingMenu ? 'Edit Menu Link' : 'Add Menu Link'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Home, Downloads, About"
                    className="rounded-xl"
                    data-testid="menu-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="url">URL</Label>
                  <Input
                    id="url"
                    value={formData.url}
                    onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://wingsdiet.com"
                    className="rounded-xl"
                    data-testid="menu-url-input"
                  />
                  <p className="text-xs text-slate-400">
                    External links will open in a new tab
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="order">Order</Label>
                  <Input
                    id="order"
                    type="number"
                    min="0"
                    value={formData.order}
                    onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                    placeholder="0"
                    className="rounded-xl"
                    data-testid="menu-order-input"
                  />
                  <p className="text-xs text-slate-400">
                    Lower numbers appear first in the menu
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="btn-primary rounded-full text-white"
                  data-testid="save-menu-btn"
                >
                  {saving ? 'Saving...' : editingMenu ? 'Update' : 'Add'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Menu Link</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this menu link? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminMenus;
